"use client";

import { useMemo, useState } from "react";

import Image from "next/image";

import { getBookingInstate } from "@repo/ui/api/getBookingInstate";
import { removeSssrsFromBookingInState } from "@repo/ui/api/removeSssrsFromBookingInState";
import { sellSSRs } from "@repo/ui/api/sellSsrs";
import { useExtrasStatus } from "@repo/ui/components/Extras/common/useExtrasStatus";
import type { Direction } from "@repo/ui/constants/directions";
import { DIRECTION_LABELS, DIRECTIONS } from "@repo/ui/constants/directions";
import { SectionScrollIds } from "@repo/ui/constants/scrollIds";
import { SSR_CODES } from "@repo/ui/constants/ssrs";
import { getDirection } from "@repo/ui/helpers/Extras/getDirection";
import { getTabSessionId } from "@repo/ui/lib/tabSessionId";
import type { CommonInfoSection, PriorityBoardingSection } from "@repo/ui/types/cms";
import type { JourneyWithPassengerSegments } from "@repo/ui/types/extras";
import type { BookingSummary } from "@repo/ui/types/manageBooking";

import { useAppDispatch, useAppSelector } from "@repo/store/hooks";
import {
  clearPriorityBoarding,
  selectBookingDetails,
  selectIsCheckIn,
  setBookingDetails,
  setPriorityBoardingButtonStatus,
  useInboundExtrasSelection,
  useOutboundExtrasSelection,
} from "@repo/store/slices";

import ExtrasPriorityBoardingPassCard from "./ExtrasPriorityBoardingPassCard";

interface ExtrasPriorityBoardingSectionCardProps {
  journeys?: JourneyWithPassengerSegments[];
  cmsData?: PriorityBoardingSection;
  commonInfo?: CommonInfoSection;
  retrieveBooking?: BookingSummary | null;
}

export default function ExtrasPriorityBoardingSectionCard({
  journeys,
  cmsData,
  commonInfo,
  retrieveBooking,
}: Readonly<ExtrasPriorityBoardingSectionCardProps>) {
  const dispatch = useAppDispatch();
  const outboundExtras = useOutboundExtrasSelection();
  const inboundExtras = useInboundExtrasSelection();
  const isCheckin = useAppSelector(selectIsCheckIn);
  const bookingDetails = useAppSelector(selectBookingDetails);
  const [loadingKeys, setLoadingKeys] = useState<string[]>([]);
  const checkingStatus = useExtrasStatus();

  // Derived state to track which passenger/direction already has priority boarding added
  const addedPriorityBoarding = useMemo(() => {
    if (!journeys || journeys.length === 0) return {};

    const computed: Record<string, boolean> = {};
    for (const [jIndex, journey] of journeys.entries()) {
      const direction = getDirection(jIndex);
      const isOutbound = direction === DIRECTIONS.OUTBOUND;
      const paxList = Object.values(journey.passengerSegment);
      for (const pax of paxList) {
        const passengerKey = pax.passengerKey;
        const pbData = isOutbound
          ? outboundExtras?.priorityBoarding?.[passengerKey]
          : inboundExtras?.priorityBoarding?.[passengerKey];
        const isPbAdded = pbData !== undefined && (typeof pbData === "number" ? pbData > 0 : true);
        computed[`${passengerKey}_${direction}`] = isPbAdded;
      }
    }
    return computed;
  }, [journeys, outboundExtras?.priorityBoarding, inboundExtras?.priorityBoarding]);

  const handleToggle = async (
    passengerKey: string,
    price: number,
    direction: Direction,
    ssrKey?: string,
    ssrCode?: string,
  ) => {
    const isOutbound = direction === DIRECTIONS.OUTBOUND;
    const map = isOutbound ? outboundExtras?.priorityBoarding : inboundExtras?.priorityBoarding;

    const pbData = map?.[passengerKey];
    const isPbAdded = pbData !== undefined && (typeof pbData === "number" ? pbData > 0 : true);
    const isAdding = !isPbAdded;

    const baseKey = `${passengerKey}_${direction}`;
    const loadingKey = `${baseKey}_${isAdding ? "add" : "remove"}`;

    try {
      setLoadingKeys((prev) => [...prev, loadingKey]);
      const tabSessionId = getTabSessionId();
      const currencyCode = bookingDetails?.currencyCode ?? "CLP";

      if (isAdding) {
        if (ssrKey) {
          await sellSSRs([ssrKey], currencyCode, tabSessionId);
        }
      } else {
        if (ssrKey) {
          await removeSssrsFromBookingInState({ ssrKeys: [ssrKey] }, tabSessionId);
        }
      }

      const booking = await getBookingInstate(tabSessionId);
      if (booking) {
        dispatch(setBookingDetails(booking));
      }

      if (isAdding) {
        dispatch(
          setPriorityBoardingButtonStatus({
            isAdded: true,
            price: price,
            direction: direction,
            passengerId: passengerKey,
            ssrKey: ssrKey,
            cartType: isCheckin ? "checkInCart" : "bookingCart",
            ssrCode: ssrCode ?? SSR_CODES.PRIORITY_BOARDING,
          }),
        );
      } else {
        dispatch(
          clearPriorityBoarding({
            direction,
            passengerKey,
            cartType: isCheckin ? "checkInCart" : "bookingCart",
          }),
        );
      }
    } catch (error) {
      if (process.env.NODE_ENV === "development") {
        console.error("[PriorityBoarding] handleToggle error:", error);
      }
    } finally {
      setLoadingKeys((prev) => prev.filter((k) => k !== loadingKey));
    }
  };

  return (
    <div
      className="flex flex-col-reverse my-3 md:flex-row"
      id={SectionScrollIds.PriorityBoarding}
      data-testid="extra-priority-boarding-section"
    >
      <div className="flex flex-col w-full p-4 space-y-4 bg-white rounded-xl">
        <div className="flex items-start gap-4">
          {cmsData?.heading?.icon && (
            <Image
              src={cmsData?.heading?.icon}
              width={50}
              height={50}
              alt="logo"
              data-testid="extra-priority-boarding-section-cms-icon"
            />
          )}
          <div className="flex flex-col space-y-1">
            {cmsData?.heading?.headline && (
              <h3
                className="text-xl font-bold text-brand-blue"
                data-testid="extra-priority-boarding-section-cms-headling-lbl"
              >
                {cmsData?.heading?.headline}
              </h3>
            )}
            {cmsData?.heading?.ctaText && (
              <span
                className="text-sm font-normal text-brand-blue"
                data-testid="extra-priority-boarding-section-cms-cta-lbl"
              >
                {cmsData?.heading?.ctaText}
              </span>
            )}
          </div>
        </div>

        <div className="space-y-2">
          <div className="w-full">
            <div
              className={
                journeys?.length === 1
                  ? "flex justify-center w-full"
                  : "grid grid-cols-1 gap-4 xl:grid-cols-2"
              }
              data-testid="extras-priority-boarding-journeys-wrapper-section"
            >
              {journeys?.map((journey, index) => {
                const direction = getDirection(index);
                const directionLabel =
                  direction === DIRECTIONS.OUTBOUND
                    ? commonInfo?.outboundLabel || DIRECTION_LABELS.OUTBOUND
                    : commonInfo?.returnLabel || DIRECTION_LABELS.INBOUND;

                const isJourneyDisabled =
                  direction === DIRECTIONS.OUTBOUND
                    ? checkingStatus?.isOutboundClose
                    : checkingStatus?.isInboundClose;

                return (
                  <div
                    key={`${journey.journeyKey} ${index} ${direction}`}
                    className={
                      journeys?.length === 1
                        ? "w-full md:w-1/2 max-w-[600px]"
                        : "w-full flex-[1_1_100%] sm:flex-[1_1_100%]"
                    }
                    data-testid={`extras-priority-boarding-journey-${journey.journeyKey}-section`}
                  >
                    <ExtrasPriorityBoardingPassCard
                      direction={direction}
                      directionLabel={directionLabel}
                      options={journey.passengerSegment}
                      bundleCode={journey.bundleCode}
                      addedPriorityBoarding={addedPriorityBoarding}
                      cmsData={cmsData}
                      commonInfo={commonInfo}
                      onChangeAction={(
                        passengerKey: string,
                        price: string | number,
                        ssrKey?: string,
                        ssrCode?: string,
                      ) => {
                        const option = Object.values(journey.passengerSegment).find(
                          (opt) => opt.passengerKey === passengerKey,
                        );
                        if (option) {
                          handleToggle(passengerKey, Number(price), direction, ssrKey, ssrCode);
                        }
                      }}
                      passengers={retrieveBooking?.passengers}
                      loadingKeys={loadingKeys}
                      isJourneyDisabled={isJourneyDisabled}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
